import { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Camera, ArrowRight, Check, X, FileText, Clock, UserX, Palette, Shield, Users, Rocket, Image as ImageIcon, GripVertical, Heart, Eye, ListChecks, Upload, Settings, StickyNote, Wrench, Briefcase, Home, Calendar, Sparkles } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { useAuth } from '@/lib/auth';

import { ThemeToggle } from '@/components/ThemeToggle';
import { getAppUrl, isLandingDomain } from '@/lib/domainConfig';
import { useSEO } from '@/hooks/useSEO';
import { SEO_CONFIGS } from '@/lib/seo';
import { MarketingHeader } from '@/components/MarketingHeader';
import { supabase } from '@/integrations/supabase/client';

export default function Landing() {
  const { user, loading: authLoading } = useAuth();
  const { t, i18n } = useTranslation('landing');
  const { t: tNiche } = useTranslation('niche');
  const { t: tCommon } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [coreFeatureTab, setCoreFeatureTab] = useState<'client' | 'field'>('field');
  const [latestPosts, setLatestPosts] = useState<{ slug: string; title: string; description: string; reading_minutes: number }[]>([]);

  useSEO(SEO_CONFIGS.landing);

  useEffect(() => {
    const lang = i18n.language?.split('-')[0] || 'en';
    const now = new Date().toISOString();
    const fetchPosts = async () => {
      let { data } = await supabase
        .from('blog_posts')
        .select('slug, title, description, reading_minutes')
        .eq('locale', lang)
        .not('published_at', 'is', null)
        .lte('published_at', now)
        .order('published_at', { ascending: false })
        .limit(3);
      if (!data || data.length === 0) {
        const fallback = await supabase
          .from('blog_posts')
          .select('slug, title, description, reading_minutes')
          .eq('locale', 'en')
          .not('published_at', 'is', null)
          .lte('published_at', now)
          .order('published_at', { ascending: false })
          .limit(3);
        data = fallback.data;
      }
      if (data) setLatestPosts(data);
    };
    fetchPosts();
  }, [i18n.language]);

  useEffect(() => {
    const schema = {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "PrepFrame",
      "applicationCategory": "BusinessApplication",
      "operatingSystem": "Web",
      "description": "Create beautiful client briefs, collect preferences before the shoot, and stay organized on shoot day. The all-in-one tool photographers love.",
      "url": "https://www.prepframe.com",
      "screenshot": "https://www.prepframe.com/og-image.jpg",
      "featureList": [
        "Client brief creation and sharing via link",
        "Moodboard and style example gallery",
        "Shoot-day field view with must-have shots",
        "PDF export (Call Sheet & Moodboard)",
        "Custom photographer branding (logo, colors, fonts)",
        "8 languages supported",
        "No client app download required"
      ],
      "offers": [
        {
          "@type": "Offer",
          "name": "Free",
          "price": "0",
          "priceCurrency": "EUR",
          "description": "Up to 3 active projects, basic brief templates"
        },
        {
          "@type": "Offer",
          "name": "Premium Monthly",
          "price": "10.99",
          "priceCurrency": "EUR",
          "description": "Unlimited projects, PDF export, custom branding"
        },
        {
          "@type": "Offer",
          "name": "Premium Annual",
          "price": "107.88",
          "priceCurrency": "EUR",
          "description": "Unlimited projects, PDF export, custom branding — billed annually"
        },
        {
          "@type": "Offer",
          "name": "Founding Member",
          "price": "199",
          "priceCurrency": "EUR",
          "description": "Lifetime access — all Premium features forever, 10 GB storage"
        }
      ]
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schema);
    script.id = 'software-app-schema';
    document.head.appendChild(script);
    return () => {
      const existing = document.getElementById('software-app-schema');
      if (existing) existing.remove();
    };
  }, []);

  const navigate = useNavigate();

  useEffect(() => {
    if (authLoading) return;
    if (user) {
      if (!searchParams.get('site')) {
        navigate('/dashboard', { replace: true });
      }
    }
  }, [user, authLoading, navigate, searchParams]);

  const handleTabChange = useCallback((value: string) => {
    setCoreFeatureTab(value as 'client' | 'field');
  }, []);

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent" />
      </div>
    );
  }

  return <div className="min-h-screen bg-background">
      <MarketingHeader />

      <main>

        {/* Hero */}
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/[0.02] via-transparent to-primary/[0.04] dark:from-primary/[0.03] dark:to-primary/[0.06]" />
          
          <div className="container relative py-16 md:py-24">
            <div className="max-w-2xl mx-auto text-center animate-fade-in">
              <h1 className="text-hero md:text-hero-lg mb-4">
                {t('hero_title_line1')}
                <br />
                <span className="text-muted-foreground">{t('hero_title_line2')}</span>
              </h1>
              
              <p className="text-base md:text-lg font-medium text-foreground/90 mb-3">
                {t('hero_subtitle')}
              </p>
              <p className="text-sm text-muted-foreground mb-8 max-w-lg mx-auto">
                {t('hero_description')}
              </p>
              
              <div className="flex flex-wrap justify-center gap-3 mb-8">
                {user ? <Button size="lg" asChild>
                    {isLandingDomain() ? <a href={getAppUrl('/dashboard')}>
                        {t('go_to_dashboard')}
                        <ArrowRight className="h-4 w-4" />
                      </a> : <Link to="/dashboard">
                        {t('go_to_dashboard')}
                        <ArrowRight className="h-4 w-4" />
                      </Link>}
                  </Button> : <>
                    <Button size="lg" asChild>
                      {isLandingDomain() ? <a href={getAppUrl('/auth?mode=signup')}>
                          {t('get_started_free')}
                          <ArrowRight className="h-4 w-4" />
                        </a> : <Link to="/auth?mode=signup">
                          {t('get_started_free')}
                          <ArrowRight className="h-4 w-4" />
                        </Link>}
                    </Button>
                    <Button size="lg" variant="outline" asChild>
                      <Link to="/demo">{t('view_demo')}</Link>
                    </Button>
                  </>}
              </div>
              
              <div className="flex flex-wrap justify-center gap-x-5 gap-y-2 text-xs text-muted-foreground">
                <span className="flex items-center gap-1.5">
                  <UserX className="h-3 w-3 text-muted-foreground/60" />
                  {t('trust_no_login')}
                </span>
                <span className="flex items-center gap-1.5">
                  <Clock className="h-3 w-3 text-muted-foreground/60" />
                  {t('trust_2_minutes')}
                </span>
                <span className="flex items-center gap-1.5">
                  <ImageIcon className="h-3 w-3 text-muted-foreground/60" />
                  {t('trust_moodboards')}
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section id="how-it-works" className="border-y border-border/50 bg-muted/30 py-16">
          <div className="container">
            <h2 className="text-display text-center mb-12">{t('how_it_works')}</h2>
            
            <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
              <div className="flex items-start gap-4">
                <div className="flex flex-col items-center">
                  <div className="h-10 w-10 rounded-full bg-foreground text-background flex items-center justify-center text-sm font-semibold shrink-0">1</div>
                  <div className="hidden md:block w-px h-full bg-border/50 mt-3" />
                </div>
                <div className="pt-1">
                  <h3 className="font-semibold mb-2">{t('step1_title')}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{t('step1_desc')}</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="flex flex-col items-center">
                  <div className="h-10 w-10 rounded-full bg-foreground text-background flex items-center justify-center text-sm font-semibold shrink-0">2</div>
                  <div className="hidden md:block w-px h-full bg-border/50 mt-3" />
                </div>
                <div className="pt-1">
                  <h3 className="font-semibold mb-2">{t('step2_title')}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{t('step2_desc')}</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="flex flex-col items-center">
                  <div className="h-10 w-10 rounded-full bg-foreground text-background flex items-center justify-center text-sm font-semibold shrink-0">3</div>
                </div>
                <div className="pt-1">
                  <h3 className="font-semibold mb-2">{t('step3_title')}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{t('step3_desc')}</p>
                </div>
              </div>
            </div>
            
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <Link to="/client-brief" className="text-sm text-muted-foreground hover:text-foreground transition-colors underline-offset-4 hover:underline">
                {t('learn_client_briefs')}
              </Link>
              <Link to="/field-view" className="text-sm text-muted-foreground hover:text-foreground transition-colors underline-offset-4 hover:underline">
                {t('learn_field_view')}
              </Link>
              <Link to="/projects" className="text-sm text-muted-foreground hover:text-foreground transition-colors underline-offset-4 hover:underline">
                {t('learn_projects')}
              </Link>
            </div>
          </div>
        </section>

        {/* Core Feature: Client Brief & Field View */}
        <section className="py-16 md:py-20">
          <div className="mx-auto w-full max-w-5xl px-6">
            <div className="text-center mb-8">
              <Badge variant="outline" className="mb-3 text-xs gap-1.5">
                <Eye className="h-3 w-3" />
                {t('core_feature')}
              </Badge>
              <Tabs value={coreFeatureTab} onValueChange={handleTabChange} className="w-full">
                <TabsList className="grid w-full max-w-xs mx-auto grid-cols-2">
                  <TabsTrigger value="client" className="gap-1.5 text-xs sm:text-sm">
                    <Users className="h-3.5 w-3.5" />
                    <span className="hidden sm:inline">{t('client_brief')}</span>
                    <span className="sm:hidden">{t('client_brief_short')}</span>
                  </TabsTrigger>
                  <TabsTrigger value="field" className="gap-1.5 text-xs sm:text-sm">
                    <Camera className="h-3.5 w-3.5" />
                    <span className="hidden sm:inline">{t('field_view')}</span>
                    <span className="sm:hidden">{t('field_view_short')}</span>
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-[300px_minmax(0,1fr)] items-center gap-8 lg:gap-10">
              <div className="relative w-full max-w-[260px] sm:max-w-[280px] lg:max-w-[300px] mx-auto lg:mx-0 aspect-[9/19] overflow-hidden rounded-3xl border border-border shadow-sm bg-muted/20">
                <img src={coreFeatureTab === 'client' ? new URL('@/assets/landing/client-brief-demo.png', import.meta.url).href : "/landing/field-view.webp"} alt={coreFeatureTab === 'client' ? "PrepFrame client brief — photographer shares a visual questionnaire with clients before the shoot" : "PrepFrame field view — photographer's shoot-day guide with must-have shots and client moodboard"} className="w-full h-full object-contain transition-opacity duration-300" loading="eager" fetchPriority="high" decoding="async" />
              </div>
              
              <div className="space-y-6">
                {coreFeatureTab === 'client' ? <>
                    <div>
                      <h2 className="text-2xl md:text-3xl font-semibold mb-3">{t('client_brief_title')}</h2>
                      <p className="text-muted-foreground">{t('client_brief_desc')}</p>
                    </div>
                    
                    <ul className="space-y-3">
                      <li className="flex items-start gap-3">
                        <div className="h-6 w-6 rounded-md bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                          <UserX className="h-3.5 w-3.5 text-primary" />
                        </div>
                        <div>
                          <p className="font-medium text-sm">{t('no_account')}</p>
                          <p className="text-xs text-muted-foreground">{t('no_account_desc')}</p>
                        </div>
                      </li>
                      <li className="flex items-start gap-3">
                        <div className="h-6 w-6 rounded-md bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                          <FileText className="h-3.5 w-3.5 text-primary" />
                        </div>
                        <div>
                          <p className="font-medium text-sm">{t('custom_templates')}</p>
                          <p className="text-xs text-muted-foreground">{t('custom_templates_desc')}</p>
                        </div>
                      </li>
                      <li className="flex items-start gap-3">
                        <div className="h-6 w-6 rounded-md bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                          <ListChecks className="h-3.5 w-3.5 text-primary" />
                        </div>
                        <div>
                          <p className="font-medium text-sm">{t('must_haves')}</p>
                          <p className="text-xs text-muted-foreground">{t('must_haves_desc')}</p>
                        </div>
                      </li>
                      <li className="flex items-start gap-3">
                        <div className="h-6 w-6 rounded-md bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                          <Heart className="h-3.5 w-3.5 text-primary" />
                        </div>
                        <div>
                          <p className="font-medium text-sm">{t('style_voting')}</p>
                          <p className="text-xs text-muted-foreground">{t('style_voting_desc')}</p>
                        </div>
                      </li>
                    </ul>
                  </> : <>
                    <div>
                      <h2 className="text-2xl md:text-3xl font-semibold mb-3">{t('field_view_title')}</h2>
                      <p className="text-muted-foreground">{t('field_view_desc')}</p>
                    </div>
                    
                    <div className="flex flex-wrap gap-2">
                      <Badge variant="secondary" className="text-xs">{t('badge_must_haves')}</Badge>
                      <Badge variant="secondary" className="text-xs">{t('badge_moodboard')}</Badge>
                      <Badge variant="secondary" className="text-xs">{t('badge_gear')}</Badge>
                      <Badge variant="secondary" className="text-xs">{t('badge_notes')}</Badge>
                    </div>
                    
                    <ul className="space-y-3">
                      <li className="flex items-start gap-3">
                        <div className="h-6 w-6 rounded-md bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                          <Settings className="h-3.5 w-3.5 text-primary" />
                        </div>
                        <div>
                          <p className="font-medium text-sm">{t('configurable_sections')}</p>
                          <p className="text-xs text-muted-foreground">{t('configurable_sections_desc')}</p>
                        </div>
                      </li>
                      <li className="flex items-start gap-3">
                        <div className="h-6 w-6 rounded-md bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                          <Wrench className="h-3.5 w-3.5 text-primary" />
                        </div>
                        <div>
                          <p className="font-medium text-sm">{t('gear_checklist')}</p>
                          <p className="text-xs text-muted-foreground">{t('gear_checklist_desc')}</p>
                        </div>
                      </li>
                      <li className="flex items-start gap-3">
                        <div className="h-6 w-6 rounded-md bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                          <StickyNote className="h-3.5 w-3.5 text-primary" />
                        </div>
                        <div>
                          <p className="font-medium text-sm">{t('private_notes')}</p>
                          <p className="text-xs text-muted-foreground">{t('private_notes_desc')}</p>
                        </div>
                      </li>
                      <li className="flex items-start gap-3">
                        <div className="h-6 w-6 rounded-md bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                          <GripVertical className="h-3.5 w-3.5 text-primary" />
                        </div>
                        <div>
                          <p className="font-medium text-sm">{t('drag_reorder')}</p>
                          <p className="text-xs text-muted-foreground">{t('drag_reorder_desc')}</p>
                        </div>
                      </li>
                    </ul>
                  </>}
                
                <div className="pt-2">
                    <Button asChild>
                      {isLandingDomain() ? <a href={getAppUrl('/auth?mode=signup')}>
                          {t('get_started_free')}
                          <ArrowRight className="h-4 w-4" />
                        </a> : <Link to="/auth?mode=signup">
                          {t('get_started_free')}
                          <ArrowRight className="h-4 w-4" />
                        </Link>}
                    </Button>
                  </div>
              </div>
            </div>
          </div>
        </section>

        {/* Mini Social Proof */}
        <section className="container py-10">
          <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6">
            <Badge variant="secondary" className="px-4 py-2 text-sm font-normal gap-2">
              <Camera className="h-3.5 w-3.5" />
              {t('proof_built_for_photographers')}
            </Badge>
            <Badge variant="secondary" className="px-4 py-2 text-sm font-normal gap-2">
              <Shield className="h-3.5 w-3.5" />
              {t('proof_privacy_first')}
            </Badge>
            <Badge variant="secondary" className="px-4 py-2 text-sm font-normal gap-2">
              <Palette className="h-3.5 w-3.5" />
              {t('proof_fully_brandable')}
            </Badge>
          </div>
        </section>

        {/* Niche brief templates section */}
        <section className="border-y border-border/50 bg-muted/30 py-16 md:py-20">
          <div className="container">
            <h2 className="text-display text-center mb-3">{tNiche('section_title')}</h2>
            <p className="text-center text-sm text-muted-foreground mb-10 max-w-xl mx-auto">{tNiche('section_desc')}</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto">
              {[
                { key: 'wedding', path: '/wedding-photography-brief', icon: Heart },
                { key: 'portrait', path: '/portrait-photography-brief', icon: Camera },
                { key: 'newborn', path: '/newborn-photography-brief', icon: Sparkles },
                { key: 'commercial', path: '/commercial-photography-brief', icon: Briefcase },
                { key: 'real_estate', path: '/real-estate-photography-brief', icon: Home },
                { key: 'event', path: '/event-photography-brief', icon: Calendar },
              ].map(({ key, path, icon: Icon }) => (
                <Link
                  key={key}
                  to={path}
                  className="flex items-start gap-3 p-4 rounded-xl border border-border/50 bg-background hover:bg-muted/50 hover:border-border transition-colors group"
                >
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 group-hover:bg-primary/15 transition-colors">
                    <Icon className="h-4.5 w-4.5 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium text-sm leading-snug mb-0.5">{tNiche(`${key}.card_title`)}</p>
                    <p className="text-xs text-muted-foreground leading-snug">{tNiche(`${key}.card_desc`)}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* From the blog section */}
        <section className="container py-16 md:py-20">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-display">{tCommon('from_the_blog')}</h2>
              <Link to="/blog" className="text-sm text-primary hover:underline inline-flex items-center gap-1">
                {tCommon('all_articles')} <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
            <div className="divide-y divide-border/50">
              {latestPosts.map(({ slug, title, description, reading_minutes }) => (
                <Link
                  key={slug}
                  to={`/blog/${slug}`}
                  className="flex items-start justify-between gap-4 py-5 first:pt-0 group"
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm leading-snug mb-1 group-hover:text-primary transition-colors">{title}</p>
                    <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2">{description}</p>
                  </div>
                  <div className="shrink-0 flex items-center gap-1 text-xs text-muted-foreground mt-0.5">
                    <Clock className="h-3 w-3" />
                    <span>{reading_minutes} {tCommon('min_read')}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="container py-16 md:py-20">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-display text-center mb-10">{t('faq_title')}</h2>
            
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="account">
                <AccordionTrigger className="text-left">{t('faq_account_q')}</AccordionTrigger>
                <AccordionContent className="text-muted-foreground">{t('faq_account_a')}</AccordionContent>
              </AccordionItem>
              <AccordionItem value="uploads">
                <AccordionTrigger className="text-left">{t('faq_uploads_q')}</AccordionTrigger>
                <AccordionContent className="text-muted-foreground">{t('faq_uploads_a')}</AccordionContent>
              </AccordionItem>
              <AccordionItem value="free">
                <AccordionTrigger className="text-left">{t('faq_free_q')}</AccordionTrigger>
                <AccordionContent className="text-muted-foreground">{t('faq_free_a')}</AccordionContent>
              </AccordionItem>
              <AccordionItem value="premium">
                <AccordionTrigger className="text-left">{t('faq_premium_q')}</AccordionTrigger>
                <AccordionContent className="text-muted-foreground">{t('faq_premium_a')}</AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </section>

      </main>

      {/* Footer */}
      <footer className="border-t border-border/50 py-8">
        <div className="container">
          <div className="flex flex-wrap justify-center gap-6 mb-6 pb-6 border-b border-border/30">
            <Link to="/client-brief" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
              {t('footer_client_brief')}
            </Link>
            <Link to="/field-view" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
              {t('footer_field_view')}
            </Link>
            <Link to="/projects" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
              {t('footer_projects')}
            </Link>
            <Link to="/demo" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
              {t('footer_demo')}
            </Link>
            <Link to="/pricing" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
              {t('footer_pricing')}
            </Link>
          </div>
          
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
            <p>© {new Date().getFullYear()} LeLu Labs s.r.o. · PrepFrame</p>
            <nav className="flex items-center gap-4">
              <Link to="/privacy" className="hover:text-foreground transition-colors">{t('footer_privacy')}</Link>
              <Link to="/terms" className="hover:text-foreground transition-colors">{t('footer_terms')}</Link>
              <Link to="/contact" className="hover:text-foreground transition-colors">{t('footer_contact')}</Link>
            </nav>
          </div>
        </div>
      </footer>

    </div>;
}
